import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SidecarModeProvider, useSidecarMode } from './context/SidecarModeContext';
import { NavigationProvider } from './context/NavigationContext';
import PrivateRoute from './components/common/PrivateRoute';
import BrandingProvider from './components/common/BrandingProvider';
import Sidebar from './components/common/Sidebar';
import LiquidGlassHeader from './components/common/LiquidGlassHeader';
import TaskProgressBar from './components/common/TaskProgressBar';
import Toast from './components/common/Toast';
import { HelpProvider, HelpPanel } from './components/HelpPanel';
import RadarBurstOverlay from './components/common/RadarBurstOverlay';
import SidecarRouteGuard from './components/common/SidecarRouteGuard';
import ErrorBoundary from './components/common/ErrorBoundary';
import { BUILD_VERSION } from './version';
import Breadcrumb from './components/navigation/Breadcrumb';
import NavigationHydrator from './components/navigation/NavigationHydrator';
import LoginPage from './pages/auth/LoginPage';
import SetPasswordPage from './pages/auth/SetPasswordPage';
import ChemistryDashboard from './pages/ChemistryDashboard';
import ProjectsPage from './pages/chemistry/ProjectsPage';
import FilesPage from './pages/chemistry/FilesPage';
import EditorPage from './pages/chemistry/EditorPage';
import HeatmapPopoutPage from './pages/chemistry/HeatmapPopoutPage';
import DebugPopoutPage from './pages/chemistry/DebugPopoutPage';
import PatentMapPopoutPage from './pages/chemistry/PatentMapPopoutPage';

import OmicsPage from './pages/omics/OmicsPage';
import ProteinDetailPage from './pages/protein/ProteinDetailPage';
import MoleculeDetailPage from './pages/molecule/MoleculeDetailPage';

import ReactionsHubPage from './pages/chemistry/reactions/ReactionsHubPage';
import ReactionDetailPage from './pages/chemistry/reactions/ReactionDetailPage';
import ImportPreviewPage from './pages/chemistry/reactions/ImportPreviewPage';

import QuickSearchPage from './pages/chemistry/QuickSearchPage';
import RegistryPage from './pages/chemistry/RegistryPage';
import InventoryPage from './pages/inventory/InventoryPage';
import BatchesPage from './pages/batches/BatchesPage';
import PipelineDashboard from './pages/pipeline/PipelineDashboard';
import GenomicsPage from './pages/GenomicsPage';
import UnifiedImportDialog from './components/import/UnifiedImportDialog';
import { isDocumentRepositoryDropTarget } from './utils/dropRouting';
import DebugPanel from './components/debug/DebugPanel';
import SharedFileLanding from './pages/shared/SharedFileLanding';
import PrivacyPage from './pages/PrivacyPage';
import ConsentBanner from './components/common/ConsentBanner';
import { useUIStore } from './stores/uiStore';
import { useFeatureStore } from './stores/featureStore';
import { isAcceptedFile } from './utils/fileDetector';
import { initActivityTracking, trackPageView } from './utils/activityTracker';
import AdminLayout from './pages/admin/AdminLayout';
import UsersPage from './pages/admin/UsersPage';
import TenantsPage from './pages/admin/TenantsPage';
import TenantDetailPage from './pages/admin/TenantDetailPage';
import AuditPage from './pages/admin/AuditPage';
import SecurityAlertsPage from './pages/admin/SecurityAlertsPage';
import SharingActivityPage from './pages/admin/SharingActivityPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import ReferenceLibraryPage from './pages/admin/ReferenceLibraryPage';
import FieldDefaultsPage from './pages/admin/FieldDefaultsPage';
import OrgSettingsPage from './pages/admin/OrgSettingsPage';
import RegistrationRulesPage from './pages/admin/RegistrationRulesPage';
import AssayTypesPage from './pages/admin/AssayTypesPage';
import WorkbenchLanding from './pages/workbench/WorkbenchLanding';
import WorkbenchIC50 from './pages/workbench/WorkbenchIC50';
import WorkbenchKinetics from './pages/workbench/WorkbenchKinetics';
import WorkbenchLibrary from './pages/workbench/WorkbenchLibrary';
import WorkbenchDocking from './pages/workbench/WorkbenchDocking';
import WorkbenchSPR from './pages/workbench/WorkbenchSPR';
import WorkbenchConformers from './pages/workbench/WorkbenchConformers';
import WorkbenchQuantum from './pages/workbench/WorkbenchQuantum';
import WorkbenchADMET from './pages/workbench/WorkbenchADMET';
import WorkbenchCalcs from './pages/workbench/WorkbenchCalcs';
import WorkbenchPKPD from './pages/workbench/WorkbenchPKPD';
import WorkbenchAbout from './pages/workbench/WorkbenchAbout';
import WorkbenchMetabolism from './pages/workbench/WorkbenchMetabolism';
import WorkbenchPadel from './pages/workbench/WorkbenchPadel';
import WorkbenchRDKitDescriptors from './pages/workbench/WorkbenchRDKitDescriptors';
import HubLauncher from './pages/workbench/HubLauncher';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import GlobalFilesPage from './pages/GlobalFilesPage';
import SpectraWorkspace from './pages/SpectraWorkspace';
import PlateHeatmapDemo from './pages/biology/PlateHeatmapDemo';
import AssayListPage from './pages/biology/AssayListPage';
import AssayDetailPage from './pages/biology/AssayDetailPage';
import AssayQueuePage from './pages/biology/AssayQueuePage';
import { ColourPaletteProvider } from './context/ColourPaletteContext';
import './styles/theme.css';
import './styles/ExternalModeTransitions.css';
import './App.css';

// Apply saved theme immediately — before React mounts — to prevent flash
(() => {
  try {
    const saved = localStorage.getItem('theme');
    const isDark = saved === 'dark';
    document.body.classList.toggle('theme-dark', isDark);
    document.body.classList.toggle('theme-light', !isDark);
    document.documentElement.classList.toggle('theme-dark', isDark);
    document.documentElement.classList.toggle('dark', isDark);
  } catch {
    document.body.classList.add('theme-light');
    document.documentElement.classList.add('theme-light');
  }
})();

// Redirect /hubs/:hubId → /workbench/:hubId (v1.2.159)
const HubsRedirect = () => {
  const { hubId } = useParams();
  return <Navigate to={`/workbench/${hubId}`} replace />;
};

// Opt-out module gate: renders children if enabled, redirects to /dashboard if disabled.
const ModuleGate: React.FC<{ modKey: string; children: React.ReactNode }> = ({ modKey, children }) => {
  const { isModuleEnabled, loaded } = useFeatureStore();
  // On a hard refresh the authenticated tenant is restored before its feature
  // flags finish loading. Do not treat that short loading window as an explicit
  // module denial, otherwise valid deep links are replaced with /dashboard.
  if (!loaded) return <div role="status" style={{ padding: 32 }}>Loading module…</div>;
  if (!isModuleEnabled(modKey)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Load feature flags for the authenticated tenant and clear them on logout or
// tenant changes. This must live above the router so route changes do not
// repeatedly reset the store.
const FeatureBootstrap: React.FC = () => {
  const { user } = useAuth();
  const fetchFeatures = useFeatureStore((state) => state.fetchFeatures);
  const resetFeatures = useFeatureStore((state) => state.resetFeatures);

  React.useEffect(() => {
    resetFeatures();
    if (user?.tenant_id != null) {
      void fetchFeatures();
    }
  }, [user?.tenant_id, fetchFeatures, resetFeatures]);

  return null;
};

// Main app layout with sidebar + global drag-to-import
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { sidecarMode } = useSidecarMode();
  const { user } = useAuth();
  const location = useLocation();
  const popoutParam = new URLSearchParams(window.location.search).get('popout');
  const isPopout = popoutParam === 'true' || popoutParam === 'tr';
  const hostname = window.location.hostname;
  const isQaEnvironment = hostname === 'anomer-qa.adroitai.ai' || hostname.startsWith('anomer-qa.');
  const isAnomerEnvironment = hostname === 'anomer-demo.adroitai.ai' || hostname.startsWith('anomer-demo.') || isQaEnvironment;
  const isBioAscentDemo = hostname === 'bioascent-demo.adroitai.ai' || hostname.startsWith('bioascent-demo.') ||
    hostname === 'bioascent-preview.adroitai.ai' || hostname.startsWith('bioascent-preview.');
  const isUploadDialogOpen = useUIStore((s) => s.isUploadDialogOpen);
  const uploadDialogProjectId = useUIStore((s) => s.uploadDialogProjectId);
  const openUploadDialog = useUIStore((s) => s.openUploadDialog);
  const closeUploadDialog = useUIStore((s) => s.closeUploadDialog);
  const [globalDragActive, setGlobalDragActive] = React.useState(false);
  const [droppedFile, setDroppedFile] = React.useState<File | null>(null);
  const dragCountRef = React.useRef(0);

  // Global drag handlers: any file dropped on the app opens the import dialog
  const onGlobalDragEnter = React.useCallback((e: React.DragEvent) => {
    // Only handle file drags (ignore text/link drags)
    if (!e.dataTransfer.types.includes('Files') || isDocumentRepositoryDropTarget(e.target)) return;
    e.preventDefault();
    dragCountRef.current++;
    if (!isUploadDialogOpen) setGlobalDragActive(true);
  }, [isUploadDialogOpen]);

  const onGlobalDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCountRef.current--;
    if (dragCountRef.current <= 0) {
      setGlobalDragActive(false);
      dragCountRef.current = 0;
    }
  }, []);

  const onGlobalDragOver = React.useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files') || isDocumentRepositoryDropTarget(e.target)) return;
    e.preventDefault();
  }, []);

  const onGlobalDrop = React.useCallback((e: React.DragEvent) => {
    // Project document repositories own their drop events. Never reinterpret
    // PDFs, presentations, or other documents as chemistry imports.
    if (e.defaultPrevented || isDocumentRepositoryDropTarget(e.target)) return;
    e.preventDefault();
    setGlobalDragActive(false);
    dragCountRef.current = 0;
    const files = e.dataTransfer.files;
    if (files.length > 0 && isAcceptedFile(files[0])) {
      setDroppedFile(files[0]);
      openUploadDialog();
    }
  }, [openUploadDialog]);

  const handleDialogClose = React.useCallback(() => {
    closeUploadDialog();
    setDroppedFile(null);
  }, [closeUploadDialog]);

  return (
    <div
      className={`app-layout ${sidecarMode ? 'sidecar-mode-active' : ''} ${isPopout ? 'app-layout--popout' : ''}`}
      onDragEnter={onGlobalDragEnter}
      onDragLeave={onGlobalDragLeave}
      onDragOver={onGlobalDragOver}
      onDrop={onGlobalDrop}
    >
      <ConsentBanner />
      {!isPopout && <RadarBurstOverlay />}
      <Toast />
      {!isPopout && <HelpPanel />}
      {!isPopout && <Sidebar />}
      <div className={`app-layout__main ${isPopout ? 'app-layout__main--popout' : ''}`}>
        {!isPopout && <LiquidGlassHeader />}
        {!isPopout && isQaEnvironment && (
          <div
            role="status"
            aria-label="Anomer QA test environment"
            className="flex items-center justify-center gap-2 border-y border-amber-300 bg-amber-100 px-4 py-1.5 text-xs font-bold tracking-[0.12em] text-amber-950"
          >
            <span aria-hidden="true">⚠</span>
            ANOMER QA · TEST ENVIRONMENT · DUMMY DATA ONLY
          </div>
        )}
        {!isPopout && isBioAscentDemo && (
          <div
            role="status"
            aria-label="BioAscent demonstration environment"
            className="flex items-center justify-center gap-2 border-y border-[#BC5D49]/40 bg-[#F7ECE9] px-4 py-1.5 text-xs font-bold tracking-[0.12em] text-[#1C284C]"
          >
            <span aria-hidden="true">◇</span>
            BIOASCENT DEMO · DEMONSTRATION ENVIRONMENT · SYNTHETIC DATA
          </div>
        )}
        {!isPopout && <TaskProgressBar />}
        {!isPopout && <Breadcrumb />}
        {!isPopout && <NavigationHydrator />}
        <main className="app-layout__content">
          <ErrorBoundary key={location.pathname}>
            <SidecarRouteGuard>
              {children}
            </SidecarRouteGuard>
          </ErrorBoundary>
        </main>
      </div>

      {/* Global drop overlay */}
      {globalDragActive && !isUploadDialogOpen && (
        <div className="fixed inset-0 z-40 bg-teal-500/10 border-4 border-dashed border-teal-400 pointer-events-none flex items-center justify-center">
          <div className="bg-white/95 rounded-2xl px-8 py-6 shadow-xl text-center">
            <p className="text-lg font-semibold text-teal-700">Drop file to import</p>
            <p className="text-sm text-gray-500 mt-1">SDF, MOL, CSV, XLSX, RXN, FASTA, PPTX &hellip;</p>
          </div>
        </div>
      )}

      {/* Unified Import Dialog */}
      <UnifiedImportDialog
        isOpen={isUploadDialogOpen}
        onClose={handleDialogClose}
        projectId={uploadDialogProjectId ?? undefined}
        initialFile={droppedFile}
      />

      {/* Admin Debug Panel — opened from Help; no floating launcher. */}
      {!isPopout && <DebugPanel userRole={(user?.role as any) || 'guest'} />}

      {/* Build version stamp */}
      <div style={{ position: 'fixed', bottom: 4, left: 8, fontSize: '10px', color: '#999', zIndex: 9999, pointerEvents: 'none' }}>
        {BUILD_VERSION}
      </div>
    </div>
  );
};

// Protected route wrapper with layout
const ProtectedPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PrivateRoute>
    <AppLayout>{children}</AppLayout>
  </PrivateRoute>
);

const RootRoute: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    navigate(isAuthenticated ? '/chemistry' : '/login', { replace: true });
  }, [isAuthenticated, loading, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
      color: '#0f172a',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 40,
          height: 40,
          margin: '0 auto 16px',
          border: '3px solid rgba(13, 148, 136, 0.18)',
          borderTopColor: '#0D9488',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <div style={{ fontSize: 16, fontWeight: 600 }}>Opening Adroit...</div>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Persist last path and project to localStorage on every navigation
const NavigationMemory: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const fullPath = location.pathname + location.search;
    // Don't memorise auth pages
    if (location.pathname === '/login' || location.pathname === '/set-password') return;
    localStorage.setItem('discoverant_last_path', fullPath);
    // Extract project id from ?project= param
    const match = location.search.match(/[?&]project=(\d+)/);
    if (match) {
      localStorage.setItem('discoverant_last_project', match[1]);
    }
    // Track page view for activity analytics
    const area = location.pathname.split('/')[1] || 'home';
    trackPageView(area);
  }, [location]);

  // Init activity tracking once (visibility change → session_end)
  useEffect(() => { initActivityTracking(); }, []);

  return null;
};

// App routes wrapped with SidecarModeProvider (must be inside Router for navigation)
const AppRoutes: React.FC = () => (
  <SidecarModeProvider>
    <NavigationProvider>
    <NavigationMemory />
    <Routes>
      {/* Auth pages - no auth required, no sidebar */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/shared/:token" element={<SharedFileLanding />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/popout/heatmap" element={<HeatmapPopoutPage />} />
      <Route path="/popout/debug" element={<DebugPopoutPage />} />
      <Route path="/popout/patent-map/:fileId" element={<PatentMapPopoutPage />} />

      {/* Protected routes with layout */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/dashboard" element={<ProtectedPage><DashboardPage /></ProtectedPage>} />
      <Route path="/profile" element={<ProtectedPage><ProfilePage /></ProtectedPage>} />
      <Route path="/chemistry" element={<ProtectedPage><ChemistryDashboard /></ProtectedPage>} />
      <Route path="/chemistry/projects" element={<ProtectedPage><ProjectsPage /></ProtectedPage>} />
      <Route path="/files" element={<ProtectedPage><GlobalFilesPage /></ProtectedPage>} />
      <Route path="/chemistry/files" element={<ProtectedPage><FilesPage /></ProtectedPage>} />
      <Route path="/chemistry/file/:id/editor" element={<ProtectedPage><EditorPage /></ProtectedPage>} />
      <Route path="/chemistry/file/:id/patents" element={<Navigate to="/chemistry" replace />} />
      <Route path="/chemistry/quick-search" element={<ProtectedPage><QuickSearchPage /></ProtectedPage>} />
      <Route path="/chemistry/registry" element={<ProtectedPage><ModuleGate modKey="mod_registry"><RegistryPage /></ModuleGate></ProtectedPage>} />
      <Route path="/inventory" element={<ProtectedPage><InventoryPage /></ProtectedPage>} />
      <Route path="/batches" element={<ProtectedPage><ModuleGate modKey="mod_batches"><BatchesPage /></ModuleGate></ProtectedPage>} />
      <Route path="/genomics" element={<ProtectedPage><GenomicsPage /></ProtectedPage>} />
      <Route path="/pipeline" element={<ProtectedPage><ModuleGate modKey="mod_pipeline"><PipelineDashboard /></ModuleGate></ProtectedPage>} />
      <Route path="/chemistry/reactions" element={<ProtectedPage><ReactionsHubPage /></ProtectedPage>} />
      <Route path="/chemistry/reactions/import-preview" element={<ProtectedPage><ImportPreviewPage /></ProtectedPage>} />
      <Route path="/chemistry/reactions/:reactionId" element={<ProtectedPage><ReactionDetailPage /></ProtectedPage>} />
      <Route path="/omics" element={<ProtectedPage><ModuleGate modKey="mod_targets"><OmicsPage /></ModuleGate></ProtectedPage>} />
      <Route path="/knowledge" element={<Navigate to="/chemistry" replace />} />
      <Route path="/protein/:id" element={<ProtectedPage><ModuleGate modKey="mod_targets"><ProteinDetailPage /></ModuleGate></ProtectedPage>} />
      <Route path="/molecule/:id" element={<ProtectedPage><MoleculeDetailPage /></ProtectedPage>} />
      <Route path="/external-intelligence" element={<Navigate to="/chemistry" replace />} />
      <Route path="/landscape" element={<Navigate to="/chemistry" replace />} />

      {/* Workbench routes */}
      <Route path="/workbench" element={<Navigate to="/hubs" replace />} />
      <Route path="/workbench/ic50" element={<ProtectedPage><WorkbenchIC50 /></ProtectedPage>} />
      <Route path="/workbench/kinetics" element={<ProtectedPage><WorkbenchKinetics /></ProtectedPage>} />
      <Route path="/workbench/library" element={<ProtectedPage><WorkbenchLibrary /></ProtectedPage>} />
      <Route path="/workbench/docking" element={<ProtectedPage><WorkbenchDocking /></ProtectedPage>} />
      <Route path="/workbench/spr" element={<ProtectedPage><WorkbenchSPR /></ProtectedPage>} />
      <Route path="/workbench/conformers" element={<ProtectedPage><WorkbenchConformers /></ProtectedPage>} />
      <Route path="/workbench/quantum" element={<ProtectedPage><WorkbenchQuantum /></ProtectedPage>} />
      <Route path="/workbench/admet" element={<ProtectedPage><WorkbenchADMET /></ProtectedPage>} />
      <Route path="/workbench/calcs" element={<ProtectedPage><WorkbenchCalcs /></ProtectedPage>} />
      <Route path="/workbench/pkpd" element={<ProtectedPage><WorkbenchPKPD /></ProtectedPage>} />
      <Route path="/workbench/metabolism" element={<ProtectedPage><WorkbenchMetabolism /></ProtectedPage>} />
      <Route path="/workbench/padel" element={<ProtectedPage><WorkbenchPadel /></ProtectedPage>} />
      <Route path="/workbench/rdkit-descriptors" element={<ProtectedPage><WorkbenchRDKitDescriptors /></ProtectedPage>} />
      <Route path="/workbench/about" element={<ProtectedPage><WorkbenchAbout /></ProtectedPage>} />
      <Route path="/workbench/:hubName" element={<ProtectedPage><HubLauncher /></ProtectedPage>} />

      {/* /hubs redirects — routes removed in v1.2.159, redirect stale bookmarks */}
      <Route path="/hubs" element={<ProtectedPage><WorkbenchLanding /></ProtectedPage>} />
      <Route path="/hubs/:hubId" element={<HubsRedirect />} />

      {/* Spectra workspace */}
      <Route path="/spectra" element={<ProtectedPage><SpectraWorkspace /></ProtectedPage>} />

      {/* Biology / Assays */}
      <Route path="/biology/plates/demo" element={<ProtectedPage><ModuleGate modKey="mod_assays"><PlateHeatmapDemo /></ModuleGate></ProtectedPage>} />
      <Route path="/biology/assays" element={<ProtectedPage><ModuleGate modKey="mod_assays"><AssayListPage /></ModuleGate></ProtectedPage>} />
      <Route path="/biology/assays/register" element={<ProtectedPage><ModuleGate modKey="mod_assays"><AssayTypesPage registrationOnly /></ModuleGate></ProtectedPage>} />
      <Route path="/biology/assays/:assayId" element={<ProtectedPage><ModuleGate modKey="mod_assays"><AssayDetailPage /></ModuleGate></ProtectedPage>} />
      <Route path="/biology/assay-queue" element={<ProtectedPage><ModuleGate modKey="mod_assays"><AssayQueuePage /></ModuleGate></ProtectedPage>} />

      {/* Admin routes - protected with main app shell */}
      <Route path="/admin" element={<ProtectedPage><AdminLayout /></ProtectedPage>}>
        <Route index element={<Navigate to="/admin/users" replace />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="sharing" element={<SharingActivityPage />} />
        <Route path="reference-library" element={<ReferenceLibraryPage />} />
        <Route path="field-defaults" element={<FieldDefaultsPage />} />
        <Route path="security" element={<SecurityAlertsPage />} />
        <Route path="org-settings" element={<OrgSettingsPage />} />
        <Route path="registration-rules" element={<RegistrationRulesPage />} />
        <Route path="assay-types" element={<AssayTypesPage />} />
      </Route>

      {/* Fallback - redirect to Chemistry Dashboard */}
      <Route path="*" element={<Navigate to="/chemistry" replace />} />
    </Routes>
    </NavigationProvider>
  </SidecarModeProvider>
);

function App() {
  return (
    <AuthProvider>
      <FeatureBootstrap />
      <ColourPaletteProvider>
        <HelpProvider>
          <BrandingProvider>
            <Router>
              <AppRoutes />
            </Router>
          </BrandingProvider>
        </HelpProvider>
      </ColourPaletteProvider>
    </AuthProvider>
  );
}

export default App;
